import { getLocalJitsiAudioTrack } from '../base/tracks/functions.any';
import { showErrorNotification } from '../notifications/actions.any';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { NoiseSuppressionEffect } from '../stream-effects/noise-suppression/NoiseSuppressionEffect';
import { SET_NOISE_SUPPRESSION_ENABLED, SET_NOISE_SUPPRESSION_ENABLED_STATE_ONLY } from './actionTypes';
import { canEnableNoiseSuppression, isNoiseSuppressionEnabled } from './functions';
import logger from './logger';
/**
 * Updates the noise suppression active state.
 *
 * @param {boolean} enabled - Is noise suppression enabled.
 * @returns {{
 *      type: SET_NOISE_SUPPRESSION_STATE,
 *      enabled: boolean
 * }}
 */
export function setNoiseSuppressionEnabledState(enabled) {
    return {
        type: SET_NOISE_SUPPRESSION_ENABLED,
        enabled
    };
}
export function setNoiseSuppressionEnabledStateOnly(enabled) {
    return {
        type: SET_NOISE_SUPPRESSION_ENABLED_STATE_ONLY,
        enabled
    };
}
/**
 *  Enabled noise suppression depending on the current state.
 *
 * @returns {Function}
 */
export function enableNoiseSuppression() {
    return (dispatch, getState) => {
        if (!isNoiseSuppressionEnabled(getState())) {
            dispatch(setNoiseSuppressionEnabled(true));
        }
        else {
        }
    };
}
/**
 *  Disable noise suppression depending on the current state.
 *
 * @returns {Function}
 */
export function disableNoiseSuppression() {
    return (dispatch, getState) => {
        if (isNoiseSuppressionEnabled(getState())) {
            dispatch(setNoiseSuppressionEnabled(false));
        }
    };
}
/**
 *  Enabled/disable noise suppression depending on the current state.
 *
 * @returns {Function}
 */
export function toggleNoiseSuppression() {
    return (dispatch, getState) => {
        if (isNoiseSuppressionEnabled(getState())) {
            dispatch(setNoiseSuppressionEnabled(false));
        }
        else {
            dispatch(setNoiseSuppressionEnabled(true));
        }
    };
}
/**
 * Attempt to enable or disable noise suppression using the {@link NoiseSuppressionEffect}.
 *
 * @param {boolean} enabled - Enable or disable noise suppression.
 *
 * @returns {Function}
 */
export function setNoiseSuppressionEnabled(enabled, enabledStateOnly = false) {
    return async (dispatch, getState) => {
        const state = getState();
        const { noiseSuppression: nsOptions } = state['features/base/config'];
        const localAudio = getLocalJitsiAudioTrack(state);
        const noiseSuppressionEnabled = isNoiseSuppressionEnabled(state);
        logger.info(`Attempting to set noise suppression enabled state: ${enabled}`);
        if (enabled === noiseSuppressionEnabled) {
            logger.warn(`Noise suppression enabled state already: ${enabled}`);
            return;
        }
        // If there is no local audio, simply set the enabled state. Once an audio track is created
        // the effects list will be applied.
        if (!localAudio) {
            if (enabledStateOnly) {
                dispatch(setNoiseSuppressionEnabledStateOnly(enabled));
            }
            else {
                dispatch(setNoiseSuppressionEnabledState(enabled));
            }
            return;
        }
        try {
            if (enabled) {
                if (!canEnableNoiseSuppression(state, dispatch, localAudio)) {
                    return;
                }
                await localAudio.setEffect(new NoiseSuppressionEffect(nsOptions));
                if (enabledStateOnly) {
                    dispatch(setNoiseSuppressionEnabledStateOnly(true));
                }
                else {
                    dispatch(setNoiseSuppressionEnabledState(true));
                }
                logger.info('Noise suppression enabled.');
            }
            else {
                await localAudio.setEffect(undefined);
                if (enabledStateOnly) {
                    dispatch(setNoiseSuppressionEnabledStateOnly(false));
                }
                else {
                    dispatch(setNoiseSuppressionEnabledState(false));
                }
                logger.info('Noise suppression disabled.');
            }
        }
        catch (error) {
            logger.error(`Failed to set noise suppression enabled to: ${enabled}`, error);
            dispatch(showErrorNotification({
                titleKey: 'notify.noiseSuppressionFailedTitle'
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        }
    };
}
